import { useMutation } from '@apollo/client';
import type { DeleteEnterpriseRiskMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  DeleteEnterpriseRiskDocument,
  namedOperations,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useDeleteEnterpriseRiskTRPC } from './useDeleteEnterpriseRiskTRPC';

/**
 * Flag-gated wrapper (RSP-6498). Delegates to the tRPC twin when the `trpc`
 * flag is on, else the legacy Apollo Hasura-action path. Consumers import THIS,
 * never the *TRPC twin.
 */
export const useDeleteEnterpriseRisk = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const trpcMutation = useDeleteEnterpriseRiskTRPC();

  const [deleteGraphQL, graphqlState] = useMutation(
    DeleteEnterpriseRiskDocument,
    {
      update: (cache) => {
        evictField(cache, 'enterprise_risk');
      },
      refetchQueries: [
        namedOperations.Query.getEnterpriseRiskById,
        namedOperations.Query.getEnterpriseRisksFlat,
        namedOperations.Query.getEnterpriseRisksByTier,
        namedOperations.Query.getEnterpriseRisks,
      ],
    }
  );

  const deleteEnterpriseRisk = async (
    id: string
  ): Promise<DeleteEnterpriseRiskMutation | undefined> => {
    if (trpcEnabled) {
      return trpcMutation.deleteEnterpriseRisk(id);
    }

    const result = await deleteGraphQL({ variables: { Id: id } });

    return result.data ?? undefined;
  };

  if (trpcEnabled) {
    return {
      deleteEnterpriseRisk,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    deleteEnterpriseRisk,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
